<script setup lang="ts">
import BuyButton from "../common/BuyButton.vue";
import FavoriteButton from "../profiles/FavoriteButton.vue";
import ProfileCoverPopover from "./ProfileCoverPopover.vue";
import ProfileLanguageToggle from "./ProfileLanguageToggle.vue";
import { PropType, ref, reactive, watch, computed, onMounted } from "vue";
import { Trainer, TranslatedProduct } from "../../types/PersologOnlineTypes.ts";
import { useWindowSize } from "../../composables/useWindowSize";
import { ProfileAdministrationAPI } from "../../api";
import { keycloak } from "../../main.ts";
import { useI18n } from "vue-i18n";
import { BENCHMARK_JOBMATCH } from "../../utils/ProjectUtils.ts";

const props = defineProps({
    products: {
        type: Object as () => Record<string, TranslatedProduct[]>,
        required: true,
    },
    layout: {
        type: String as PropType<"list" | "grid" | undefined>,
        default: "list",
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const layout = ref(props.layout);
const { t, locale } = useI18n();

const emit = defineEmits(["update", "layout"]);

function handleUpdate(product: string) {
    emit("update", true, product);
}

async function favoriteProduct(product: string) {
    await ProfileAdministrationAPI.favoriteProduct(keycloak.tokenParsed?.sub as string, product as string);
    emit("update", true, product);
}

watch(
    () => props.layout,
    (newLayout: "list" | "grid" ) => {
        layout.value = newLayout;
    },
);

watch(
    () => layout.value,
    () => {
        emit("layout", layout.value);
    },
);

// always switch to grid on mobile
const windowSize = useWindowSize();
const isDesktop = computed(() => windowSize.width >= 640);
watch(isDesktop, (newIsDesktop) => {
    if (newIsDesktop) {
        layout.value = props.layout ?? "list";
    } else {
        layout.value = "grid";
    }
});
onMounted(() => (layout.value = isDesktop.value ? (props.layout ?? "list") : "grid"));

// ─── Cover images (grid view)

const coverImages = reactive<Record<string, string | null>>({});

function loadCoverImage(productNo: string) {
    if (productNo in coverImages) return;
    const isDE = locale.value.startsWith("de");
    const imgLocale = isDE ? "de" : "en";
    const basePath = "./images/profile-cover/";
    const tryDeFallbackPng = () => {
        const img = new globalThis.Image();
        img.onload = () => { coverImages[productNo] = img.src; };
        img.onerror = () => { coverImages[productNo] = null; };
        img.src = `${basePath}de-${productNo}.png`;
    };
    const tryDeFallback = () => {
        const img = new globalThis.Image();
        img.onload = () => { coverImages[productNo] = img.src; };
        img.onerror = tryDeFallbackPng;
        img.src = `${basePath}de-${productNo}.jpg`;
    };
    const tryPng = () => {
        const img = new globalThis.Image();
        img.onload = () => { coverImages[productNo] = img.src; };
        img.onerror = () => {
            if (isDE) {
                coverImages[productNo] = null;
            } else {
                tryDeFallback();
            }
        };
        img.src = `${basePath}${imgLocale}-${productNo}.png`;
    };
    const imgJpg = new globalThis.Image();
    imgJpg.onload = () => { coverImages[productNo] = imgJpg.src; };
    imgJpg.onerror = tryPng;
    imgJpg.src = `${basePath}${imgLocale}-${productNo}.jpg`;
}

function loadAllCoverImages() {
    Object.values(props.products).flat().forEach((p) => loadCoverImage(p.productNo));
}

onMounted(loadAllCoverImages);
watch(() => props.products, loadAllCoverImages);


</script>

<template>
    <div v-for="(productGroup, modelName) in props.products" :key="modelName + '-all'" class="my-4 buy-profiles-data-view">
        <DataView
            :value="productGroup"
            :layout="layout"
            data-key="id"
            :pt="{ header: { style: 'background: var(--p-secondary-500); padding: 0.75rem 1rem;' } }"
        >
            <template #header>
                <div class="flex justify-between items-center">
                    <h2 class="m-0 ml-4 text-white">{{ modelName }}</h2>
                </div>
            </template>

            <template #list="slotProps">
                <div
                    v-for="(item, index) in slotProps.items"
                    :key="index"
                    class="product-card w-full grid grid-cols-12 gap-4 mx-1 p-2"
                    :id="'data-view-products_data-view_' + item.productNo + '_card'"
                >
                    <div class="col-span-8">
                        <div
                            class="mb-0 text-2xl font-bold text-content flex items-center justify-start gap-1"
                        >
                            <span :id="'data-view-products_data-view_' + item.productNo + '_name'">{{
                                item.productName
                            }}</span>
                            <ProfileCoverPopover
                                :product-no="item.productNo"
                                :product-name="item.productName"
                                :profile-languages="item.profileLanguages"
                                :questionnaire-languages="item.questionnaireLanguages"
                                :trainer-language="props.trainer.language"
                            />
                            <FavoriteButton :product="item.productNo" @update="handleUpdate(item.productNo)" />
                        </div>
                        <ProfileLanguageToggle
                            :product-no="item.productNo"
                            :profile-languages="item.profileLanguages"
                            :questionnaire-languages="item.questionnaireLanguages"
                            :trainer-language="props.trainer.language"
                            :toggle-id="'data-view-products_div_' + item.productNo + '_language-toggle'"
                            :fieldset-id="'data-view-products_fieldset_' + item.productNo + '_languages'"
                        />
                    </div>
                    <div class="col-span-4 flex justify-end">
                        <BuyButton
                            v-if="item.productNo !== BENCHMARK_JOBMATCH"
                            :product="item.productNo"
                            :product-name="item.productName"
                            :trainer="props.trainer"
                            :shop-link="item.shopLink"
                            @update="handleUpdate(item.productNo)"
                        />
                        <Button
                            v-else
                            :id="'buy-button_btn_' + item.productNo"
                            :outlined="true"
                            icon="pi pi-heart"
                            :label="t('project-configuration.useButton')"
                            @click="favoriteProduct(item.productNo)"
                        />
                    </div>
                </div>
            </template>

            <template #grid="slotProps">
                <div
                    v-for="(item, index) in slotProps.items"
                    :key="index"
                    class="product-card"
                    :id="'data-view-products_data-view_' + item.productNo + '_card'"
                >
                    <!-- Mobile: vertical layout with info button, no languages -->
                    <div v-if="!isDesktop" class="p-4 flex flex-col border-border-ui bg-surface h-full">
                        <div class="cover-img-container mb-4">
                            <Image
                                v-if="coverImages[item.productNo]"
                                :src="coverImages[item.productNo] ?? undefined"
                                :alt="item.productName"
                                preview
                                image-class="cover-img"
                            />
                            <div v-else class="cover-placeholder">
                                <i class="pi pi-image" />
                                <span class="text-xs text-center">{{ item.productName }}</span>
                            </div>
                        </div>
                        <div class="flex items-start gap-1 mb-2 flex-wrap">
                            <FavoriteButton
                                :product="item.productNo"
                                @update="handleUpdate(item.productNo)"
                            />
                            <span
                                :id="'data-view-products_data-view_' + item.productNo + '_name'"
                                class="text-xl font-bold text-content flex-1"
                            >{{ item.productName }}</span>
                            <ProfileCoverPopover
                                :product-no="item.productNo"
                                :product-name="item.productName"
                                :profile-languages="item.profileLanguages"
                                :questionnaire-languages="item.questionnaireLanguages"
                                :trainer-language="props.trainer.language"
                            />
                        </div>
                        <div
                            class="mt-auto"
                            :id="'data-view-products_btn_' + item.productNo + '_buy'"
                        >
                            <BuyButton
                                v-if="item.productNo !== BENCHMARK_JOBMATCH"
                                :product="item.productNo"
                                :product-name="item.productName"
                                :trainer="trainer"
                                :shop-link="item.shopLink"
                                @update="handleUpdate(item.productNo)"
                            />
                            <Button
                                v-else
                                :id="'buy-button_btn_' + item.productNo"
                                :outlined="true"
                                icon="pi pi-heart"
                                :label="t('project-configuration.useButton')"
                                @click="favoriteProduct(item.productNo)"
                            />
                        </div>
                    </div>

                    <!-- Desktop: two-column layout with cover left, info right -->
                    <div v-else class="p-4 flex gap-4 border-border-ui bg-surface h-full">
                        <!-- Left: cover image -->
                        <div class="card-cover-col flex flex-col items-stretch">
                            <div class="cover-img-container">
                                <Image
                                    v-if="coverImages[item.productNo]"
                                    :src="coverImages[item.productNo] ?? undefined"
                                    :alt="item.productName"
                                    preview
                                    image-class="cover-img"
                                />
                                <div v-else class="cover-placeholder">
                                    <i class="pi pi-image" />
                                    <span class="text-xs text-center">{{ item.productName }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Right: favorite, name, languages, buy button -->
                        <div class="card-info-col flex flex-col flex-1">
                            <div class="flex justify-end">
                                <FavoriteButton
                                    :product="item.productNo"
                                    @update="handleUpdate(item.productNo)"
                                />
                            </div>
                            <span
                                :id="'data-view-products_data-view_' + item.productNo + '_name'"
                                class="text-xl font-bold text-content mb-2"
                            >{{ item.productName }}</span>
                            <ProfileLanguageToggle
                                :product-no="item.productNo"
                                :profile-languages="item.profileLanguages"
                                :questionnaire-languages="item.questionnaireLanguages"
                                :trainer-language="props.trainer.language"
                                :always-expanded="true"
                                class="mb-2"
                            />
                            <div
                                class="mt-auto"
                                :id="'data-view-products_btn_' + item.productNo + '_buy'"
                            >
                                <BuyButton
                                    v-if="item.productNo !== BENCHMARK_JOBMATCH"
                                    :product="item.productNo"
                                    :product-name="item.productName"
                                    :trainer="trainer"
                                    :shop-link="item.shopLink"
                                    @update="handleUpdate(item.productNo)"
                                />
                                <Button
                                    v-else
                                    :id="'buy-button_btn_' + item.productNo"
                                    :outlined="true"
                                    icon="pi pi-heart"
                                    :label="t('project-configuration.useButton')"
                                    @click="favoriteProduct(item.productNo)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </DataView>
    </div>
</template>

<style scoped>
/* 2 columns for the buy-profiles grid view */
.buy-profiles-data-view :deep(.p-dataview-grid .p-dataview-content) {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 1rem;
    padding: 0.75rem;
}

/* Card left column */
.card-cover-col {
    width: 38%;
    flex-shrink: 0;
}

.cover-img-container {
    width: 100%;
}

.cover-img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    display: block;
    object-fit: cover;
}

/* PrimeVue Image wrapper should fill the container */
.cover-img-container :deep(.p-image) {
    display: block;
    width: 100%;
}

.cover-img-container :deep(.p-image img) {
    width: 100%;
    height: auto;
    border-radius: 4px;
    display: block;
    object-fit: cover;
    cursor: zoom-in;
}

.cover-placeholder {
    width: 100%;
    aspect-ratio: 3 / 4;
    background: var(--p-surface-100);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: var(--p-text-muted-color);
    border: 1px dashed var(--p-surface-300);
    padding: 0.5rem;
    box-sizing: border-box;
}

.cover-placeholder .pi {
    font-size: 2rem;
    opacity: 0.4;
}

/* Card right column */
.card-info-col {
    min-width: 0;
    overflow: hidden;
}

/* On small screens fall back to 1 column */
@media (max-width: 640px) {
    .buy-profiles-data-view :deep(.p-dataview-grid .p-dataview-content) {
        grid-template-columns: 1fr !important;
    }
}
</style>
